// Service quản lý localStorage
const STORAGE_KEYS = {
  DATA_SOURCES: 'dataSources',
  DATA_ENTITIES: 'dataEntities',
  VALIDATION_RULES: 'validationRules',
  UPLOADED_FILES: 'uploadedFiles',
  RULE_TYPES: 'ruleTypes'
};

class StorageService {
  // Data Sources
  getDataSources() {
    const data = localStorage.getItem(STORAGE_KEYS.DATA_SOURCES);
    return data ? JSON.parse(data) : [];
  }

  saveDataSource(dataSource) {
    const sources = this.getDataSources();
    const index = sources.findIndex(s => s.id === dataSource.id);
    
    if (index >= 0) {
      sources[index] = dataSource;
    } else {
      sources.push(dataSource);
    }
    
    localStorage.setItem(STORAGE_KEYS.DATA_SOURCES, JSON.stringify(sources));
    return dataSource;
  }

  deleteDataSource(id) {
    const sources = this.getDataSources().filter(s => s.id !== id);
    localStorage.setItem(STORAGE_KEYS.DATA_SOURCES, JSON.stringify(sources));
  }

  // Data Entities (Dictionary)
  getDataEntities() {
    const data = localStorage.getItem(STORAGE_KEYS.DATA_ENTITIES);
    return data ? JSON.parse(data) : [];
  }

  saveDataEntity(entity) {
    const entities = this.getDataEntities();
    const index = entities.findIndex(e => e.id === entity.id);
    
    if (index >= 0) {
      entities[index] = entity;
    } else {
      entities.push(entity);
    }
    
    localStorage.setItem(STORAGE_KEYS.DATA_ENTITIES, JSON.stringify(entities));
    return entity;
  }

  deleteDataEntity(id) {
    const entities = this.getDataEntities().filter(e => e.id !== id);
    localStorage.setItem(STORAGE_KEYS.DATA_ENTITIES, JSON.stringify(entities));
  }

  // Validation Rules
  getValidationRules() {
    const data = localStorage.getItem(STORAGE_KEYS.VALIDATION_RULES);
    return data ? JSON.parse(data) : [];
  }

  saveValidationRule(rule) {
    const rules = this.getValidationRules();
    const index = rules.findIndex(r => r.id === rule.id);
    
    if (index >= 0) {
      rules[index] = rule;
    } else {
      rules.push(rule);
    }
    
    localStorage.setItem(STORAGE_KEYS.VALIDATION_RULES, JSON.stringify(rules));
    return rule;
  }

  deleteValidationRule(id) {
    const rules = this.getValidationRules().filter(r => r.id !== id);
    localStorage.setItem(STORAGE_KEYS.VALIDATION_RULES, JSON.stringify(rules));
  }

  // Uploaded Files
  getUploadedFiles() {
    const data = localStorage.getItem(STORAGE_KEYS.UPLOADED_FILES);
    return data ? JSON.parse(data) : [];
  }

  saveUploadedFile(file) {
    const files = this.getUploadedFiles();
    files.push(file);
    localStorage.setItem(STORAGE_KEYS.UPLOADED_FILES, JSON.stringify(files));
    return file;
  }

  deleteUploadedFile(id) {
    const files = this.getUploadedFiles().filter(f => f.id !== id);
    localStorage.setItem(STORAGE_KEYS.UPLOADED_FILES, JSON.stringify(files));
  }

  clearAll() {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  }

  // Initialize with sample data
  initializeSampleData() {
    // Sample Data Sources
    if (this.getDataSources().length === 0) {
      // Database Source
      this.saveDataSource({
        id: 'ds1',
        name: 'Core Banking Database',
        type: 'database',
        connectionString: 'postgresql://localhost:5432/corebanking',
        tables: [
          {
            name: 'accounts',
            columns: [
              { name: 'account_id', type: 'varchar', format: 'text' },
              { name: 'account_number', type: 'varchar', format: 'text' },
              { name: 'customer_id', type: 'varchar', format: 'text' },
              { name: 'balance', type: 'decimal', format: 'number' },
              { name: 'currency', type: 'varchar', format: 'text' },
              { name: 'status', type: 'varchar', format: 'text' },
              { name: 'created_date', type: 'date', format: 'date' }
            ]
          },
          {
            name: 'cards',
            columns: [
              { name: 'card_id', type: 'varchar', format: 'text' },
              { name: 'card_number', type: 'varchar', format: 'text' },
              { name: 'card_type', type: 'varchar', format: 'text' },
              { name: 'credit_limit', type: 'decimal', format: 'number' },
              { name: 'used_amount', type: 'decimal', format: 'number' },
              { name: 'expiry_date', type: 'date', format: 'date' },
              { name: 'cvv', type: 'varchar', format: 'text' }
            ]
          },
          {
            name: 'transactions',
            columns: [
              { name: 'transaction_id', type: 'varchar', format: 'text' },
              { name: 'account_id', type: 'varchar', format: 'text' },
              { name: 'amount', type: 'decimal', format: 'number' },
              { name: 'transaction_type', type: 'varchar', format: 'text' },
              { name: 'transaction_date', type: 'date', format: 'date' },
              { name: 'description', type: 'varchar', format: 'text' }
            ]
          },
          {
            name: 'customers',
            columns: [
              { name: 'customer_id', type: 'varchar', format: 'text' },
              { name: 'full_name', type: 'varchar', format: 'text' },
              { name: 'id_number', type: 'varchar', format: 'text' },
              { name: 'phone', type: 'varchar', format: 'text' },
              { name: 'email', type: 'varchar', format: 'text' },
              { name: 'date_of_birth', type: 'date', format: 'date' },
              { name: 'address', type: 'varchar', format: 'text' }
            ]
          },
          {
            name: 'loans',
            columns: [
              { name: 'loan_id', type: 'varchar', format: 'text' },
              { name: 'customer_id', type: 'varchar', format: 'text' },
              { name: 'loan_amount', type: 'decimal', format: 'number' },
              { name: 'interest_rate', type: 'decimal', format: 'number' },
              { name: 'term_months', type: 'int', format: 'number' },
              { name: 'outstanding_balance', type: 'decimal', format: 'number' },
              { name: 'status', type: 'varchar', format: 'text' }
            ]
          }
        ],
        createdAt: new Date().toISOString()
      });

      // File Source
      this.saveDataSource({
        id: 'ds2',
        name: 'Excel Reports',
        type: 'file',
        connectionString: '/data/reports/',
        tables: [
          {
            name: 'monthly_report',
            columns: [
              { name: 'report_month', type: 'date', format: 'date' },
              { name: 'total_customers', type: 'int', format: 'number' },
              { name: 'total_transactions', type: 'int', format: 'number' },
              { name: 'total_amount', type: 'decimal', format: 'number' }
            ]
          }
        ],
        createdAt: new Date().toISOString()
      });

      // API Source
      this.saveDataSource({
        id: 'ds3',
        name: 'Credit Bureau API',
        type: 'api',
        connectionString: 'https://api.creditbureau.com/v1',
        tables: [
          {
            name: 'credit_scores',
            columns: [
              { name: 'customer_id', type: 'varchar', format: 'text' },
              { name: 'credit_score', type: 'int', format: 'number' },
              { name: 'rating', type: 'varchar', format: 'text' },
              { name: 'last_updated', type: 'date', format: 'date' }
            ]
          }
        ],
        createdAt: new Date().toISOString()
      });
    }

    // Sample Data Entities
    if (this.getDataEntities().length === 0) {
      // Thẻ tín dụng
      this.saveDataEntity({
        id: 'ent1',
        name: 'Thẻ tín dụng',
        code: 'CREDIT_CARD',
        description: 'Thông tin thẻ tín dụng của khách hàng',
        fields: [
          {
            id: 'f1',
            name: 'Số thẻ',
            code: 'card_number',
            dataType: 'string',
            format: 'text',
            mapping: {
              type: 'direct',
              source: 'cards.card_number'
            }
          },
          {
            id: 'f2',
            name: 'Hạn mức',
            code: 'credit_limit',
            dataType: 'number',
            format: 'decimal',
            mapping: {
              type: 'direct',
              source: 'cards.credit_limit'
            }
          },
          {
            id: 'f3',
            name: 'Số dư đã dùng',
            code: 'used_amount',
            dataType: 'number',
            format: 'decimal',
            mapping: {
              type: 'direct',
              source: 'cards.used_amount'
            }
          },
          {
            id: 'f4',
            name: 'Số dư khả dụng',
            code: 'available_balance',
            dataType: 'number',
            format: 'decimal',
            mapping: {
              type: 'formula',
              formula: 'credit_limit - used_amount',
              fields: ['credit_limit', 'used_amount']
            }
          },
          {
            id: 'f5',
            name: 'Ngày hết hạn',
            code: 'expiry_date',
            dataType: 'date',
            format: 'date',
            mapping: {
              type: 'direct',
              source: 'cards.expiry_date'
            }
          },
          {
            id: 'f6',
            name: 'Mã CVV',
            code: 'cvv',
            dataType: 'string',
            format: 'text',
            mapping: {
              type: 'direct',
              source: 'cards.cvv'
            }
          }
        ],
        createdAt: new Date().toISOString()
      });

      // Tài khoản
      this.saveDataEntity({
        id: 'ent2',
        name: 'Tài khoản',
        code: 'ACCOUNT',
        description: 'Thông tin tài khoản ngân hàng',
        fields: [
          {
            id: 'f7',
            name: 'Số tài khoản',
            code: 'account_number',
            dataType: 'string',
            format: 'text',
            mapping: {
              type: 'direct',
              source: 'accounts.account_number'
            }
          },
          {
            id: 'f8',
            name: 'Mã khách hàng',
            code: 'customer_id',
            dataType: 'string',
            format: 'text',
            mapping: {
              type: 'direct',
              source: 'accounts.customer_id'
            }
          },
          {
            id: 'f9',
            name: 'Số dư',
            code: 'balance',
            dataType: 'number',
            format: 'decimal',
            mapping: {
              type: 'direct',
              source: 'accounts.balance'
            }
          },
          {
            id: 'f10',
            name: 'Loại tiền',
            code: 'currency',
            dataType: 'string',
            format: 'text',
            mapping: {
              type: 'direct',
              source: 'accounts.currency'
            }
          },
          {
            id: 'f11',
            name: 'Trạng thái',
            code: 'status',
            dataType: 'string',
            format: 'text',
            mapping: {
              type: 'direct',
              source: 'accounts.status'
            }
          }
        ],
        createdAt: new Date().toISOString()
      });

      // Giao dịch
      this.saveDataEntity({
        id: 'ent3',
        name: 'Giao dịch',
        code: 'TRANSACTION',
        description: 'Thông tin giao dịch tài chính',
        fields: [
          {
            id: 'f12',
            name: 'Mã giao dịch',
            code: 'transaction_id',
            dataType: 'string',
            format: 'text',
            mapping: {
              type: 'direct',
              source: 'transactions.transaction_id'
            }
          },
          {
            id: 'f13',
            name: 'Số tiền',
            code: 'amount',
            dataType: 'number',
            format: 'decimal',
            mapping: {
              type: 'direct',
              source: 'transactions.amount'
            }
          },
          {
            id: 'f14',
            name: 'Loại giao dịch',
            code: 'transaction_type',
            dataType: 'string',
            format: 'text',
            mapping: {
              type: 'direct',
              source: 'transactions.transaction_type'
            }
          },
          {
            id: 'f15',
            name: 'Ngày giao dịch',
            code: 'transaction_date',
            dataType: 'date',
            format: 'date',
            mapping: {
              type: 'direct',
              source: 'transactions.transaction_date'
            }
          },
          {
            id: 'f16',
            name: 'Mô tả',
            code: 'description',
            dataType: 'string',
            format: 'text',
            mapping: {
              type: 'direct',
              source: 'transactions.description'
            }
          }
        ],
        createdAt: new Date().toISOString()
      });

      // Khách hàng
      this.saveDataEntity({
        id: 'ent4',
        name: 'Khách hàng',
        code: 'CUSTOMER',
        description: 'Thông tin khách hàng',
        fields: [
          {
            id: 'f17',
            name: 'Mã khách hàng',
            code: 'customer_id',
            dataType: 'string',
            format: 'text',
            mapping: {
              type: 'direct',
              source: 'customers.customer_id'
            }
          },
          {
            id: 'f18',
            name: 'Họ tên',
            code: 'full_name',
            dataType: 'string',
            format: 'text',
            mapping: {
              type: 'direct',
              source: 'customers.full_name'
            }
          },
          {
            id: 'f19',
            name: 'CMND/CCCD',
            code: 'id_number',
            dataType: 'string',
            format: 'text',
            mapping: {
              type: 'direct',
              source: 'customers.id_number'
            }
          },
          {
            id: 'f20',
            name: 'Số điện thoại',
            code: 'phone',
            dataType: 'string',
            format: 'text',
            mapping: {
              type: 'direct',
              source: 'customers.phone'
            }
          },
          {
            id: 'f21',
            name: 'Email',
            code: 'email',
            dataType: 'string',
            format: 'text',
            mapping: {
              type: 'direct',
              source: 'customers.email'
            }
          },
          {
            id: 'f22',
            name: 'Ngày sinh',
            code: 'date_of_birth',
            dataType: 'date',
            format: 'date',
            mapping: {
              type: 'direct',
              source: 'customers.date_of_birth'
            }
          }
        ],
        createdAt: new Date().toISOString()
      });

      // Khoản vay
      this.saveDataEntity({
        id: 'ent5',
        name: 'Khoản vay',
        code: 'LOAN',
        description: 'Thông tin khoản vay',
        fields: [
          {
            id: 'f23',
            name: 'Mã khoản vay',
            code: 'loan_id',
            dataType: 'string',
            format: 'text',
            mapping: {
              type: 'direct',
              source: 'loans.loan_id'
            }
          },
          {
            id: 'f24',
            name: 'Số tiền vay',
            code: 'loan_amount',
            dataType: 'number',
            format: 'decimal',
            mapping: {
              type: 'direct',
              source: 'loans.loan_amount'
            }
          },
          {
            id: 'f25',
            name: 'Lãi suất (%)',
            code: 'interest_rate',
            dataType: 'number',
            format: 'decimal',
            mapping: {
              type: 'direct',
              source: 'loans.interest_rate'
            }
          },
          {
            id: 'f26',
            name: 'Kỳ hạn (tháng)',
            code: 'term_months',
            dataType: 'number',
            format: 'number',
            mapping: {
              type: 'direct',
              source: 'loans.term_months'
            }
          },
          {
            id: 'f27',
            name: 'Dư nợ',
            code: 'outstanding_balance',
            dataType: 'number',
            format: 'decimal',
            mapping: {
              type: 'direct',
              source: 'loans.outstanding_balance'
            }
          },
          {
            id: 'f28',
            name: 'Số tiền đã trả',
            code: 'paid_amount',
            dataType: 'number',
            format: 'decimal',
            mapping: {
              type: 'formula',
              formula: 'loan_amount - outstanding_balance',
              fields: ['loan_amount', 'outstanding_balance']
            }
          },
          {
            id: 'f29',
            name: 'Trạng thái',
            code: 'status',
            dataType: 'string',
            format: 'text',
            mapping: {
              type: 'direct',
              source: 'loans.status'
            }
          }
        ],
        createdAt: new Date().toISOString()
      });
    }

    // Sample Validation Rules
    if (this.getValidationRules().length === 0) {
      // Rules cho Thẻ tín dụng
      this.saveValidationRule({
        id: 'rule1',
        entityId: 'ent1',
        entityName: 'Thẻ tín dụng',
        fieldId: 'f1',
        fieldName: 'Số thẻ',
        ruleName: 'Định dạng số thẻ',
        ruleType: 'regex',
        config: {
          pattern: '^[0-9]{16}$',
          message: 'Số thẻ phải có 16 chữ số'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      this.saveValidationRule({
        id: 'rule2',
        entityId: 'ent1',
        entityName: 'Thẻ tín dụng',
        fieldId: 'f2',
        fieldName: 'Hạn mức',
        ruleName: 'Giới hạn hạn mức',
        ruleType: 'range',
        config: {
          min: 1000000,
          max: 1000000000,
          message: 'Hạn mức phải từ 1 triệu đến 1 tỷ'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      this.saveValidationRule({
        id: 'rule3',
        entityId: 'ent1',
        entityName: 'Thẻ tín dụng',
        fieldId: 'f4',
        fieldName: 'Số dư khả dụng',
        ruleName: 'Số dư không âm',
        ruleType: 'range',
        config: {
          min: 0,
          message: 'Số dư khả dụng không được âm'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      this.saveValidationRule({
        id: 'rule4',
        entityId: 'ent1',
        entityName: 'Thẻ tín dụng',
        fieldId: 'f6',
        fieldName: 'Mã CVV',
        ruleName: 'CVV 3 chữ số',
        ruleType: 'regex',
        config: {
          pattern: '^[0-9]{3}$',
          message: 'CVV phải có đúng 3 chữ số'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      // Rules cho Tài khoản
      this.saveValidationRule({
        id: 'rule5',
        entityId: 'ent2',
        entityName: 'Tài khoản',
        fieldId: 'f7',
        fieldName: 'Số tài khoản',
        ruleName: 'Bắt buộc nhập',
        ruleType: 'required',
        config: {
          message: 'Số tài khoản là bắt buộc'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      this.saveValidationRule({
        id: 'rule6',
        entityId: 'ent2',
        entityName: 'Tài khoản',
        fieldId: 'f7',
        fieldName: 'Số tài khoản',
        ruleName: 'Định dạng số tài khoản',
        ruleType: 'regex',
        config: {
          pattern: '^[A-Z]{3}[0-9]{6,12}$',
          message: 'Số tài khoản: 3 chữ cái + 6-12 chữ số (VD: ACC001234)'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      this.saveValidationRule({
        id: 'rule7',
        entityId: 'ent2',
        entityName: 'Tài khoản',
        fieldId: 'f9',
        fieldName: 'Số dư',
        ruleName: 'Số dư hợp lệ',
        ruleType: 'range',
        config: {
          min: 0,
          max: 100000000000,
          message: 'Số dư phải từ 0 đến 100 tỷ'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      this.saveValidationRule({
        id: 'rule8',
        entityId: 'ent2',
        entityName: 'Tài khoản',
        fieldId: 'f10',
        fieldName: 'Loại tiền',
        ruleName: 'Mã tiền tệ chuẩn',
        ruleType: 'regex',
        config: {
          pattern: '^(VND|USD|EUR|JPY|CNY|GBP)$',
          message: 'Loại tiền phải là: VND, USD, EUR, JPY, CNY, hoặc GBP'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      this.saveValidationRule({
        id: 'rule9',
        entityId: 'ent2',
        entityName: 'Tài khoản',
        fieldId: 'f11',
        fieldName: 'Trạng thái',
        ruleName: 'Trạng thái hợp lệ',
        ruleType: 'regex',
        config: {
          pattern: '^(ACTIVE|INACTIVE|CLOSED|SUSPENDED)$',
          message: 'Trạng thái phải là: ACTIVE, INACTIVE, CLOSED, hoặc SUSPENDED'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      // Rules cho Giao dịch
      this.saveValidationRule({
        id: 'rule10',
        entityId: 'ent3',
        entityName: 'Giao dịch',
        fieldId: 'f12',
        fieldName: 'Mã giao dịch',
        ruleName: 'Bắt buộc nhập',
        ruleType: 'required',
        config: {
          message: 'Mã giao dịch là bắt buộc'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      this.saveValidationRule({
        id: 'rule11',
        entityId: 'ent3',
        entityName: 'Giao dịch',
        fieldId: 'f13',
        fieldName: 'Số tiền',
        ruleName: 'Số tiền dương',
        ruleType: 'range',
        config: {
          min: 0.01,
          max: 10000000000,
          message: 'Số tiền phải > 0 và <= 10 tỷ'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      this.saveValidationRule({
        id: 'rule12',
        entityId: 'ent3',
        entityName: 'Giao dịch',
        fieldId: 'f14',
        fieldName: 'Loại giao dịch',
        ruleName: 'Loại giao dịch hợp lệ',
        ruleType: 'regex',
        config: {
          pattern: '^(DEPOSIT|WITHDRAWAL|TRANSFER|PAYMENT)$',
          message: 'Loại GD: DEPOSIT, WITHDRAWAL, TRANSFER, hoặc PAYMENT'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      this.saveValidationRule({
        id: 'rule13',
        entityId: 'ent3',
        entityName: 'Giao dịch',
        fieldId: 'f16',
        fieldName: 'Mô tả',
        ruleName: 'Độ dài mô tả',
        ruleType: 'length',
        config: {
          min: 5,
          max: 200,
          message: 'Mô tả phải từ 5-200 ký tự'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      // Rules cho Khách hàng
      this.saveValidationRule({
        id: 'rule14',
        entityId: 'ent4',
        entityName: 'Khách hàng',
        fieldId: 'f18',
        fieldName: 'Họ tên',
        ruleName: 'Bắt buộc nhập họ tên',
        ruleType: 'required',
        config: {
          message: 'Họ tên là bắt buộc'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      this.saveValidationRule({
        id: 'rule15',
        entityId: 'ent4',
        entityName: 'Khách hàng',
        fieldId: 'f18',
        fieldName: 'Họ tên',
        ruleName: 'Độ dài họ tên',
        ruleType: 'length',
        config: {
          min: 3,
          max: 100,
          message: 'Họ tên phải từ 3-100 ký tự'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      this.saveValidationRule({
        id: 'rule16',
        entityId: 'ent4',
        entityName: 'Khách hàng',
        fieldId: 'f19',
        fieldName: 'CMND/CCCD',
        ruleName: 'Định dạng CMND/CCCD',
        ruleType: 'regex',
        config: {
          pattern: '^[0-9]{9,12}$',
          message: 'CMND/CCCD phải có 9-12 chữ số'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      this.saveValidationRule({
        id: 'rule17',
        entityId: 'ent4',
        entityName: 'Khách hàng',
        fieldId: 'f20',
        fieldName: 'Số điện thoại',
        ruleName: 'Định dạng số điện thoại',
        ruleType: 'regex',
        config: {
          pattern: '^(\\+84|0)[0-9]{9,10}$',
          message: 'SĐT: 0xxxxxxxxx hoặc +84xxxxxxxxx'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      this.saveValidationRule({
        id: 'rule18',
        entityId: 'ent4',
        entityName: 'Khách hàng',
        fieldId: 'f21',
        fieldName: 'Email',
        ruleName: 'Định dạng email',
        ruleType: 'regex',
        config: {
          pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
          message: 'Email không đúng định dạng'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      this.saveValidationRule({
        id: 'rule19',
        entityId: 'ent4',
        entityName: 'Khách hàng',
        fieldId: 'f22',
        fieldName: 'Ngày sinh',
        ruleName: 'Độ tuổi hợp lệ',
        ruleType: 'custom',
        config: {
          code: `
            const dob = new Date(value);
            const today = new Date();
            const age = today.getFullYear() - dob.getFullYear();
            return age >= 18 && age <= 100;
          `,
          message: 'Khách hàng phải từ 18-100 tuổi'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      // Rules cho Khoản vay
      this.saveValidationRule({
        id: 'rule20',
        entityId: 'ent5',
        entityName: 'Khoản vay',
        fieldId: 'f24',
        fieldName: 'Số tiền vay',
        ruleName: 'Giới hạn số tiền vay',
        ruleType: 'range',
        config: {
          min: 10000000,
          max: 5000000000,
          message: 'Số tiền vay từ 10 triệu đến 5 tỷ'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      this.saveValidationRule({
        id: 'rule21',
        entityId: 'ent5',
        entityName: 'Khoản vay',
        fieldId: 'f25',
        fieldName: 'Lãi suất (%)',
        ruleName: 'Lãi suất hợp lệ',
        ruleType: 'range',
        config: {
          min: 0.1,
          max: 30,
          message: 'Lãi suất từ 0.1% đến 30%'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      this.saveValidationRule({
        id: 'rule22',
        entityId: 'ent5',
        entityName: 'Khoản vay',
        fieldId: 'f26',
        fieldName: 'Kỳ hạn (tháng)',
        ruleName: 'Kỳ hạn hợp lệ',
        ruleType: 'range',
        config: {
          min: 1,
          max: 360,
          message: 'Kỳ hạn từ 1-360 tháng'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      this.saveValidationRule({
        id: 'rule23',
        entityId: 'ent5',
        entityName: 'Khoản vay',
        fieldId: 'f27',
        fieldName: 'Dư nợ',
        ruleName: 'Dư nợ hợp lệ',
        ruleType: 'custom',
        config: {
          code: 'return value >= 0;',
          message: 'Dư nợ không được âm'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });

      this.saveValidationRule({
        id: 'rule24',
        entityId: 'ent5',
        entityName: 'Khoản vay',
        fieldId: 'f29',
        fieldName: 'Trạng thái',
        ruleName: 'Trạng thái khoản vay',
        ruleType: 'regex',
        config: {
          pattern: '^(ACTIVE|PAID_OFF|DEFAULTED|RESTRUCTURED)$',
          message: 'Trạng thái: ACTIVE, PAID_OFF, DEFAULTED, hoặc RESTRUCTURED'
        },
        enabled: true,
        createdAt: new Date().toISOString()
      });
    }
  }

  // Rule Types
  getRuleTypes() {
    const data = localStorage.getItem(STORAGE_KEYS.RULE_TYPES);
    return data ? JSON.parse(data) : [];
  }

  saveRuleType(ruleType) {
    const ruleTypes = this.getRuleTypes();
    const index = ruleTypes.findIndex(rt => rt.id === ruleType.id);
    
    if (index >= 0) {
      ruleTypes[index] = ruleType;
    } else {
      ruleTypes.push(ruleType);
    }
    
    localStorage.setItem(STORAGE_KEYS.RULE_TYPES, JSON.stringify(ruleTypes));
    return ruleType;
  }

  deleteRuleType(id) {
    const ruleTypes = this.getRuleTypes().filter(rt => rt.id !== id);
    localStorage.setItem(STORAGE_KEYS.RULE_TYPES, JSON.stringify(ruleTypes));
  }
}

export default new StorageService();
